import { z } from 'zod';
import { baseString } from './core/primitives';
import { str, emptyToUndefined, booleanFlag } from './core/helpers';

// ── Constants ───────────────────────────────────────────────────────────────
const FORM_NAME_MAX_LENGTH = 255;
const FORM_DESCRIPTION_MAX_LENGTH = 2000;
const SUBFORM_ORDER_MAX = 1000;

// ── Field definitions ───────────────────────────────────────────────────────

export const formNameField = (t) =>
  z.preprocess(
    str,
    baseString
      .min(1, t('validation.form.name.required'))
      .max(
        FORM_NAME_MAX_LENGTH,
        t('validation.form.name.maxLength', { max: FORM_NAME_MAX_LENGTH })
      )
  );

export const formDescriptionField = (t) =>
  emptyToUndefined(
    baseString
      .max(
        FORM_DESCRIPTION_MAX_LENGTH,
        t('validation.form.description.maxLength', {
          max: FORM_DESCRIPTION_MAX_LENGTH,
        })
      )
      .optional()
  );

export const publishedField = (t) =>
  booleanFlag(t('validation.form.published.invalid'));

export const templateField = (t) =>
  booleanFlag(t('validation.form.published.invalid'));

export const subformItemSchema = (t) =>
  z.object({
    id: z
      .number({ error: t('validation.form.subformId.required') })
      .int(t('validation.form.subformId.invalid'))
      .positive(t('validation.form.subformId.invalid')),
    order: z
      .number({ error: t('validation.form.subformOrder.required') })
      .int(t('validation.form.subformOrder.invalid'))
      .min(0, t('validation.form.subformOrder.invalid'))
      .max(
        SUBFORM_ORDER_MAX,
        t('validation.form.subformOrder.maxValue', { max: SUBFORM_ORDER_MAX })
      )
      .optional(),
  });

export const subformsArrayField = (t) =>
  z
    .array(subformItemSchema(t), {
      error: t('validation.form.subforms.arrayRequired'),
    })
    .min(1, t('validation.form.subforms.required'))
    .refine(
      (subforms) => {
        const ids = subforms.map((s) => s.id);
        return new Set(ids).size === ids.length;
      },
      { message: t('validation.form.subforms.duplicateIds') }
    );

// ── Schemas ─────────────────────────────────────────────────────────────────

export const createFormSchema = (t) =>
  z.object({
    name: formNameField(t),
    description: formDescriptionField(t),
    published: publishedField(t).optional(),
    template: templateField(t).optional(),
    subforms: subformsArrayField(t),
  });

export const updateFormSchema = (t) =>
  z.object({
    name: formNameField(t).optional(),
    description: formDescriptionField(t),
    published: publishedField(t).optional(),
    subforms: subformsArrayField(t),
  });

export { FORM_NAME_MAX_LENGTH, FORM_DESCRIPTION_MAX_LENGTH, SUBFORM_ORDER_MAX };
